import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { postOverlay, scopeBody, num, str, dayLabel } from '@/lib/overlayApi';
import { METRIC_BY_KEY, type MetricKey } from '@/lib/metrics';
import type { DateRange } from '@/lib/dateRange';

export interface MetricSeries {
  labels: string[];
  values: Record<MetricKey, Array<number | null>>;
}

/**
 * Daily series for the selected metrics over the current scope.
 *
 * One request regardless of how many metrics are ticked — every plottable
 * metric is a column on the same daily row, so selecting five costs exactly
 * what selecting one costs. The metric selection is therefore NOT part of the
 * query key: ticking another box re-reads the cache rather than the warehouse.
 */
export function useMetricSeries(
  customerName: string,
  scope: { category?: string | undefined; city?: string | undefined; sku?: string | undefined },
  keys: MetricKey[],
  range: DateRange,
  enabled: boolean,
): UseQueryResult<MetricSeries, Error> {
  return useQuery({
    queryKey: ['metric-series', customerName, scope, range],
    enabled: enabled && keys.length > 0,
    staleTime: 5 * 60 * 1000,
    select: (rows: Array<Record<string, unknown>>): MetricSeries => {
      const values = {} as Record<MetricKey, Array<number | null>>;
      for (const k of keys) {
        const field = METRIC_BY_KEY.get(k)?.field;
        values[k] = field ? rows.map((r) => num(r[field])) : rows.map(() => null);
      }
      return { labels: rows.map((r) => dayLabel(str(r['day']))), values };
    },
    queryFn: async ({ signal }) => {
      const res = await postOverlay<Record<string, unknown>>(
        '/v1/availability/series',
        scopeBody({ customer_name: customerName, ...scope }, range),
        signal,
      );
      return [...res.rows].sort((a, b) => str(a['day']).localeCompare(str(b['day'])));
    },
  });
}
